
import * as fromSignin from './signin.reducer';
import * as fromForgotPassword from './forgot-password.reducer';
import * as fromRenewPassword from './renew-password.reducer';
import * as fromInvite from './invite.reducer';
import * as fromMfa from './mfa.reducer';

import { createFeatureSelector, createSelector, Action, combineReducers } from '@ngrx/store';

export const signinModuleFeatureKey = 'signinModule';

export interface SigninModuleState {
    [fromSignin.signinFeatureKey]: fromSignin.State;
    [fromForgotPassword.forgotPasswordFeatureKey]: fromForgotPassword.State;
    [fromRenewPassword.renewPasswordFeatureKey]: fromRenewPassword.State;
    [fromInvite.inviteFeatureKey]: fromInvite.State;
    [fromMfa.mfaFeatureKey]: fromMfa.State;
}

export interface State {
    [signinModuleFeatureKey]: SigninModuleState;
}

export function reducers(state: SigninModuleState | undefined, action: Action) {
    return combineReducers({
        [fromSignin.signinFeatureKey]: fromSignin.reducer,
        [fromForgotPassword.forgotPasswordFeatureKey]: fromForgotPassword.reducer,
        [fromRenewPassword.renewPasswordFeatureKey]: fromRenewPassword.reducer,
        [fromInvite.inviteFeatureKey]: fromInvite.reducer,
        [fromMfa.mfaFeatureKey]: fromMfa.reducer,
    })(state, action);
}

export const getSigninModuleState = createFeatureSelector<SigninModuleState>(signinModuleFeatureKey);

/** Signin */
export const getSigninState = createSelector(getSigninModuleState, state => state.signin);

export const getRedirectSsoProcessing = createSelector(getSigninState, state => state.redirectSsoProcessing);
export const getRedirectSsoError = createSelector(getSigninState, state => state.redirectSsoError);

export const getSigninConfiguration = createSelector(getSigninState, state => state.signinConfiguration);
export const getSigninConfigurationLoading = createSelector(getSigninState, state => state.signinConfigurationLoading);
export const getSigninConfigurationLoaded = createSelector(getSigninState, state => state.signinConfigurationLoaded);
export const getSigninConfigurationError = createSelector(getSigninState, state => state.signinConfigurationError);

export const getSigninWithPasswordLoading = createSelector(getSigninState, state => state.signinWithPasswordLoading);
export const getSigninWithPasswordSuccess = createSelector(getSigninState, state => state.signinWithPasswordSuccess);
export const getSigninWithPasswordError = createSelector(getSigninState, state => state.signinWithPasswordError);

/** Forgot Password  */
export const getForgotPasswordState = createSelector(getSigninModuleState, state => state.forgotPassword);

export const getForgotPasswordErrorMessage = createSelector(getForgotPasswordState, state => state.errorMessage);
export const getForgotPasswordLoading = createSelector(getForgotPasswordState, state => state.forgotPasswordLoading);
export const getForgotPasswordSuccess = createSelector(getForgotPasswordState, state => state.forgotPasswordSuccess);


/** Renew Password  */
export const getRenewPasswordState = createSelector(getSigninModuleState, state => state.renewPassword);

export const getRenewPasswordErrorMessage = createSelector(getRenewPasswordState, state => state.renewTokenErrorMessage);
export const getRenewPasswordLoading = createSelector(getRenewPasswordState, state => state.renewPasswordLoading);
export const getRenewPasswordSuccess = createSelector(getRenewPasswordState, state => state.renewPasswordSuccess);

export const getRenewPasswordToken = createSelector(getRenewPasswordState, state => state.decodedTokenMessage);
export const getRenewPasswordTokenInvalid = createSelector(getRenewPasswordState, state => state.decodedTokenMessageInvalid);

export const getRenewPasswordTokenExpired = createSelector(getRenewPasswordState, state => state.renewTokenExpired);
export const getRenewPasswordError = createSelector(getRenewPasswordState, state => state.renewPasswordErrorMessage);

/** Invitation  */
export const getInviteState = createSelector(getSigninModuleState, state => state.invite);

export const getInviteDecodedMessage = createSelector(getInviteState, state => state.decodedMessage);
export const getInviteLoading = createSelector(getInviteState, state => state.inviteLoading);
export const getInviteSuccess = createSelector(getInviteState, state => state.inviteSuccess);
export const getInviteErrorMessage = createSelector(getInviteState, state => state.errorMessage);
export const getInviteTokenExpired = createSelector(getInviteState, state => state.inviteTokenExpired);
export const getInviteTokenInvalid = createSelector(getInviteState, state => state.inviteTokenInvalid);

/** Mfa Challenge */
export const getMfaState = createSelector(getSigninModuleState, state => state.mfa);

export const getMfaRequired = createSelector(getMfaState, state => state.mfaRequired);
export const getMfaDefaultFactor = createSelector(getMfaState, state => state.defaultFactor);
export const getMfaFactors = createSelector(getMfaState, state => state.factors);
export const getMfaToken = createSelector(getMfaState, state => state.token);

export const getMfaSendOtpLoading = createSelector(getMfaState, state => state.sendOtpLoading);
export const getMfaSendOtpLoaded = createSelector(getMfaState, state => state.sendOtpLoaded);
export const getMfaSendOtpError = createSelector(getMfaState, state => state.sendOtpError);

export const getMfaverifyOtpLoading = createSelector(getMfaState, state => state.verifyOtpLoading);
export const getMfaverifyOtpLoaded = createSelector(getMfaState, state => state.verifyOtpSuccess);
export const getMfaverifyOtpError = createSelector(getMfaState, state => state.verifyOtpError);

export const getVerifyApplicationFactorLoading = createSelector(getMfaState, state => state.verifyApplicationFactorLoading);
export const getVerifyApplicationFactorLoaded = createSelector(getMfaState, state => state.verifyApplicationFactorLoaded);
export const getVerifyApplicationFactorError = createSelector(getMfaState, state => state.verifyApplicationFactorError);

export const getCreateMfaApplicationFactorSecretLoading = createSelector(getMfaState, state => state.createMfaApplicationFactorSecretLoading);
export const getCreateMfaApplicationFactorSecretLoaded = createSelector(getMfaState, state => state.createMfaApplicationFactorSecretLoaded);
export const getCreateMfaApplicationFactorSecretError = createSelector(getMfaState, state => state.createMfaApplicationFactorSecretError);
export const getMfaApplicationSecret = createSelector(getMfaState, state => state.applicationSecret);

export const getVerifyEmailFactorLoading = createSelector(getMfaState, state => state.verifyEmailFactorLoading);
export const getVerifyEmailFactorLoaded = createSelector(getMfaState, state => state.verifyEmailFactorLoaded);
export const getVerifyEmailFactorError = createSelector(getMfaState, state => state.verifyEmailFactorError);

export const getMfaRegion = createSelector(getMfaState, state => state.region);
export const getMfaError = createSelector(getMfaState, state => state.error);
